import type { ReactNode } from 'react'

/**
 * Four states, kept apart on purpose (STE-546). Empty and no-results in
 * particular must not look alike: a user who cannot tell them apart believes
 * their library has vanished.
 */

export function LoadingRows({ rows = 6, columns = 8 }: { rows?: number; columns?: number }) {
  return (
    <div aria-live="polite" aria-busy="true">
      <span className="sr-only">Loading job descriptions</span>
      {Array.from({ length: rows }, (_, r) => (
        <div className="skeleton-row" key={r} aria-hidden="true">
          {Array.from({ length: columns }, (_, c) => (
            <div
              className="skeleton"
              key={c}
              style={{ flex: c === 0 ? '2 1 0' : '1 1 0', opacity: 1 - r * 0.09 }}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

export function LoadingBlock({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="card state" aria-live="polite" aria-busy="true">
      <div className="spinner" aria-hidden="true" />
      <h2>{label}</h2>
    </div>
  )
}

/**
 * Nothing here yet, said in whatever words this screen needs.
 *
 * `EmptyLibrary` below is the job description library's own wording and stays
 * that way. This is for every other screen that has an empty state, so none of
 * them has to invent its own markup and get the shape subtly different.
 *
 * **Empty is not an error.** A customer who has not started is at the
 * beginning, and the screen says what to do next rather than what went wrong.
 */
export function Empty({ title, children, action }: {
  title: string
  children: React.ReactNode
  action?: { label: string; onClick: () => void }
}) {
  return (
    <div className="state state--empty">
      <h2>{title}</h2>
      {children}
      {action && (
        <div className="actions" style={{ justifyContent: 'center', marginTop: 4 }}>
          <button type="button" className="btn btn--primary" onClick={action.onClick}>
            {action.label}
          </button>
        </div>
      )}
    </div>
  )
}

export function EmptyLibrary({ canCreate, onCreate, onBulk }: {
  canCreate: boolean
  onCreate: () => void
  onBulk: () => void
}) {
  return (
    <div className="state state--empty">
      <h2>No job descriptions yet</h2>
      <p>
        This is where every role your company has will live. Load the whole library from a
        spreadsheet, or add the first role on its own.
      </p>
      {canCreate && (
        <div className="actions" style={{ justifyContent: 'center', marginTop: 4 }}>
          <button type="button" className="btn btn--primary" onClick={onBulk}>
            Mass upload
          </button>
          <button type="button" className="btn" onClick={onCreate}>
            Add one role
          </button>
        </div>
      )}
      {!canCreate && <p>Your HR team has not loaded the library yet.</p>}
    </div>
  )
}

export function NoResults({ query, onClear }: { query: string; onClear: () => void }) {
  return (
    <div className="state">
      <h2>No job description matches “{query}”</h2>
      <p>Try a title, job family, level, location or skill. Every word you type has to match.</p>
      <button type="button" className="btn" onClick={onClear}>
        Clear search
      </button>
    </div>
  )
}

export function ErrorState({ message, onRetry, children }: {
  message: string
  onRetry?: () => void
  children?: ReactNode
}) {
  return (
    <div className="state state--error" role="alert">
      <h2>That did not load</h2>
      <p>{message}</p>
      {children}
      {onRetry && (
        <button type="button" className="btn" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  )
}
